import { ChevronRight, FileText } from 'lucide-react';

import { EmptyState } from '@/components/ui/EmptyState';
import { subjectTheme } from '@/features/institute/timetable/subjectTheme';
import { cn } from '@/lib/utils';
import type { PaginationMeta } from '@/types/api';
import type { ParentWorkItem } from '../../types/parent';
import {
  WORK_STATE_LABEL,
  WORK_TYPE_LABEL,
  formatDueDay,
  workState,
} from '../../utils';

export interface WorkListProps {
  /** Only the rows on this page — the page above does the slicing. */
  items: ParentWorkItem[];
  meta: PaginationMeta;
  onPageChange: (page: number) => void;
  selectedUuid: string | null;
  onSelect: (uuid: string) => void;
  today: string;
  /** "assignments" or "tasks", for the heading and the range line. */
  noun: string;
  /** Offered when the filters have emptied the list. */
  onClearFilters: () => void;
  /** Whether any filter is actually in force, which decides the empty copy. */
  isFiltered: boolean;
}

/**
 * The work set for the child, newest deadline first.
 *
 * Rows are buttons, not links: opening one fills the panel beside it rather
 * than leaving the screen, so a parent can read four pieces of work without
 * losing the list they were going down.
 *
 * The subject's colour and icon come from `subjectTheme`, the same table the
 * timetable paints its cards with, so English is the same green here as it is
 * on the timetable the parent looked at a minute ago (CLAUDE.md §3).
 */
export function WorkList({
  items,
  meta,
  onPageChange,
  selectedUuid,
  onSelect,
  today,
  noun,
  onClearFilters,
  isFiltered,
}: WorkListProps) {
  const startRecord = meta.total === 0 ? 0 : (meta.page - 1) * meta.limit + 1;
  const endRecord = Math.min(meta.page * meta.limit, meta.total);

  return (
    <section
      className="rounded-2xl sm:rounded-3xl border border-slate-200/90 dark:border-slate-800 bg-white dark:bg-slate-900 overflow-hidden shadow-xs flex flex-col justify-between"
      aria-label={`${noun} list`}
    >
      <div>
        <header className="flex items-center justify-between gap-3 border-b border-slate-100 dark:border-slate-800 px-4 py-3.5 sm:px-5">
          <h2 className="text-sm sm:text-base font-bold capitalize text-slate-800 dark:text-white">
            {noun}
          </h2>
          <span className="rounded-full bg-indigo-50 dark:bg-indigo-950/60 px-2.5 py-0.5 text-xs font-bold text-indigo-600 dark:text-indigo-400">
            {meta.total}
          </span>
        </header>

        {items.length === 0 ? (
          <EmptyState
            icon={<FileText className="h-5 w-5" />}
            title={isFiltered ? `No ${noun} match these filters` : `No ${noun} yet`}
            description={
              isFiltered
                ? 'Nothing here answers to every filter at once. Widen one of them to see more.'
                : `Once the class teacher sets ${noun}, everything set for your child appears here with its due date.`
            }
            action={
              isFiltered ? (
                <button
                  type="button"
                  onClick={onClearFilters}
                  className="text-sm font-bold text-indigo-600 dark:text-indigo-400 underline-offset-4 hover:underline cursor-pointer"
                >
                  Clear the filters
                </button>
              ) : undefined
            }
          />
        ) : (
          <ul className="flex flex-col divide-y divide-slate-100 dark:divide-slate-800">
            {items.map((item) => {
              const state = workState(item, today);
              const theme = subjectTheme(item.subject);
              const isSelected = item.uuid === selectedUuid;

              return (
                <li key={item.uuid}>
                  <button
                    type="button"
                    onClick={() => onSelect(item.uuid)}
                    aria-pressed={isSelected}
                    className={cn(
                      'flex w-full items-center gap-3.5 px-4 py-3.5 text-left transition-colors sm:px-5 cursor-pointer',
                      'focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500',
                      isSelected
                        ? 'bg-indigo-50/70 dark:bg-indigo-950/40 ring-1 ring-inset ring-indigo-500/30'
                        : 'hover:bg-slate-50 dark:hover:bg-slate-800/50',
                    )}
                  >
                    <span
                      aria-hidden
                      className={cn(
                        'grid h-11 w-11 shrink-0 place-items-center rounded-xl sm:rounded-2xl',
                        theme.iconBg,
                      )}
                    >
                      {theme.icon}
                    </span>

                    <span className="min-w-0 flex-1">
                      {/* Who set it beside the subject, as the details panel
                          words it — a family knows which teacher to ask. */}
                      <span className="block truncate text-sm font-semibold text-slate-500 dark:text-slate-400">
                        <span className="font-bold text-blue-600 dark:text-blue-400">{item.subject}</span>
                        {item.teacher && <> · {item.teacher}</>}
                      </span>
                      <span className="mt-0.5 block truncate text-sm sm:text-base font-bold text-slate-800 dark:text-white">
                        {item.title}
                      </span>
                      <span className="mt-0.5 block truncate text-xs font-semibold text-slate-500 dark:text-slate-400">
                        Due {formatDueDay(item.dueDate)}
                      </span>
                    </span>

                    <span className="hidden shrink-0 items-center gap-2.5 sm:flex">
                      <span className="rounded-full bg-slate-100 dark:bg-slate-800 px-2.5 py-1 text-sm font-bold text-slate-600 dark:text-slate-300">
                        {WORK_TYPE_LABEL[item.type]}
                      </span>
                      <span
                        className={cn(
                          'inline-flex items-center px-2.5 py-0.5 rounded-full text-sm font-bold',
                          state === 'COMPLETED' &&
                            'bg-emerald-500/10 text-emerald-700 dark:text-emerald-400 border border-emerald-500/20',
                          state === 'OVERDUE' &&
                            'bg-rose-500/10 text-rose-700 dark:text-rose-400 border border-rose-500/20',
                          (state === 'PENDING' || state === 'UPCOMING') &&
                            'bg-amber-500/10 text-amber-700 dark:text-amber-400 border border-amber-500/20',
                        )}
                      >
                        {WORK_STATE_LABEL[state]}
                      </span>
                    </span>

                    {/* On mobile */}
                    <span className="shrink-0 sm:hidden">
                      <span
                        className={cn(
                          'inline-flex items-center px-2.5 py-0.5 rounded-full text-sm font-bold',
                          state === 'COMPLETED' &&
                            'bg-emerald-500/10 text-emerald-700 dark:text-emerald-400 border border-emerald-500/20',
                          state === 'OVERDUE' &&
                            'bg-rose-500/10 text-rose-700 dark:text-rose-400 border border-rose-500/20',
                          (state === 'PENDING' || state === 'UPCOMING') &&
                            'bg-amber-500/10 text-amber-700 dark:text-amber-400 border border-amber-500/20',
                        )}
                      >
                        {WORK_STATE_LABEL[state]}
                      </span>
                    </span>

                    <ChevronRight
                      className={cn(
                        'h-4 w-4 shrink-0 transition-colors',
                        isSelected ? 'text-indigo-600 dark:text-indigo-400' : 'text-slate-400 dark:text-slate-500',
                      )}
                      aria-hidden
                    />
                  </button>
                </li>
              );
            })}
          </ul>
        )}
      </div>

      {/* Pagination Footer */}
      {meta.total > 0 && (
        <div className="flex flex-wrap items-center justify-between gap-3 p-4 border-t border-slate-100 dark:border-slate-800 text-xs font-medium text-slate-500 dark:text-slate-400">
          <div>
            Showing {startRecord} to {endRecord} of {meta.total} {noun}
          </div>

          <div className="flex items-center gap-1">
            <button
              type="button"
              onClick={() => onPageChange(meta.page - 1)}
              disabled={meta.page <= 1}
              aria-label="Previous page"
              className="flex h-7 w-7 items-center justify-center rounded-lg border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800 text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors disabled:opacity-40 disabled:pointer-events-none cursor-pointer"
            >
              <span className="sr-only">Previous</span>
              &lt;
            </button>

            {Array.from({ length: meta.totalPages }, (_, i) => i + 1).map((pageNum) => {
              const isActive = pageNum === meta.page;
              return (
                <button
                  key={pageNum}
                  type="button"
                  onClick={() => onPageChange(pageNum)}
                  className={`flex h-7 w-7 items-center justify-center rounded-lg text-xs font-bold transition-colors cursor-pointer ${
                    isActive
                      ? 'bg-indigo-600 text-white shadow-xs'
                      : 'border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800 text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white hover:bg-slate-100 dark:hover:bg-slate-700'
                  }`}
                >
                  {pageNum}
                </button>
              );
            })}

            <button
              type="button"
              onClick={() => onPageChange(meta.page + 1)}
              disabled={meta.page >= meta.totalPages}
              aria-label="Next page"
              className="flex h-7 w-7 items-center justify-center rounded-lg border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800 text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors disabled:opacity-40 disabled:pointer-events-none cursor-pointer"
            >
              <span className="sr-only">Next</span>
              &gt;
            </button>
          </div>
        </div>
      )}
    </section>
  );
}
